/**
 * TryQuiPus — DB Type Definitions
 *
 * Interfaces for all Dexie table rows, extracted from localDb.ts so
 * consumers that only need types don't pull in the Dexie runtime.
 *
 * localDb.ts re-exports everything from here for backwards compatibility.
 */

import type { Company, Transaction } from './index';
import type { SerializedJournalEntry } from '../services/AccountingEngine';

// ═══════════════════════════════════════════════════════════════════
// CORE TABLE INTERFACES
// ═══════════════════════════════════════════════════════════════════

export interface DBCompany extends Company {
  dbId?: number;
  /** Clave de firma contable — requerido en escrituras v4+. */
  firmId: string;
  /** Timestamp de creación (epoch ms para Dexie, ISO string para Supabase). */
  createdAt?: number | string;
  /** Pilar 2 Gobernanza: ID del usuario que creó o actualizó el registro. */
  createdBy?: string;
}

export interface DBTransaction extends Transaction {
  /** PK auto-increment de Dexie. Usar este campo en updateTransaction(). */
  dbId?: number;
  /** Clave de firma contable — requerido en escrituras v4+. */
  firmId: string;
  /** Clave multitenant — requerido en todas las escrituras. */
  companyId: string;
  /** Mantenido por compatibilidad con componentes existentes. */
  companyRuc?: string;
  periodo?: string;
  createdAt?: string;
  /** Pilar 2 Gobernanza: ID del usuario que originó esta transacción. */
  createdBy?: string;
  /**
   * RUC del proveedor (11 dígitos) — poblado al importar XMLs SUNAT.
   * Ausente en transacciones creadas por otros medios; validación solo
   * se activa cuando el campo está presente y no vacío.
   */
  rucProveedor?: string;
  /**
   * Razón social del proveedor — poblado al importar XMLs SUNAT.
   */
  razonSocialProveedor?: string;
}

export interface DBJournalEntry extends SerializedJournalEntry {
  dbId?: number;
  /** Clave de firma contable — requerido en escrituras v4+. */
  firmId: string;
  /** Clave multitenant — requerido en todas las escrituras. */
  companyId: string;
  /** Mantenido por compatibilidad con componentes existentes. */
  companyRuc?: string;
  /** Tipo de asiento: automático (pipeline) o manual (usuario). */
  tipo?: 'automatico' | 'manual';
  createdAt?: string;
  /**
   * Gobernanza de Libro Diario:
   * - annulled: asiento anulado (no se elimina físicamente, se muestra tachado en ámbar).
   * - isDeleted: marcado como eliminado lógico (tachado en rojo, excluido de EEFF).
   * Ninguna de estas flags borra el registro de IndexedDB — audit trail permanente.
   */
  annulled?: boolean;
  isDeleted?: boolean;
  /**
   * Origen del asiento.
   * - 'auto': generado por el pipeline de facturas (se reemplaza en cada importación)
   * - 'manual': ingresado por el usuario (nunca se borra automáticamente)
   */
  source?: 'auto' | 'manual';
  /** Pilar 2 Gobernanza: ID del usuario que creó este asiento. */
  createdBy?: string;
  /**
   * Publicación a Libros Contables.
   * - posted=true: asiento publicado → aparece en Libro Diario oficial, no puede eliminarse.
   * - posted=false (default): asiento guardado pero aún no publicado.
   */
  posted: boolean;
  posted_at: string | null;
  posted_by: string | null;
  /** CUO SUNAT-compliant: {periodo}-{0001…9999} — campo #2 del PLE 5.1 y 6.1 */
  cuoPeriodo?: string;
  /** FK a transactions.id — enriquece campos 7–12 del PLE (tipo CP, serie, número). */
  transaction_id?: string;
}

export interface DBSunatRecord {
  dbId?: number;
  /** Clave de firma contable — requerido en escrituras v4+. */
  firmId: string;
  tipo: 'compra' | 'venta';
  periodo: string;
  /** Clave multitenant — requerido. */
  companyId: string;
  companyRuc: string;
  contenidoRaw: string;
  registrosParsed: number;
  importedAt: string;
  /** Pilar 2 Gobernanza: ID del usuario que generó este registro. */
  createdBy?: string;
}

export interface DBDocument {
  id?: number;
  /** Clave de firma contable. Opcional para compatibilidad con documentos previos. */
  firmId?: string;
  fileName: string;
  mimeType: string;
  blob: Blob;
  fileSize: number;
  uploadedAt: string;
  /** Clave multitenant. Opcional para compatibilidad con documentos previos. */
  companyId?: string;
  companyRuc?: string;
  periodo?: string;
  /** Pilar 2 Gobernanza: ID del usuario que subió este documento. */
  createdBy?: string;
  /** Ruta en Supabase Storage bucket "documents" (firmId/companyId/uuid-filename). */
  storagePath?: string;
  /** URL pública permanente del archivo en Supabase Storage. */
  storageUrl?: string;
}

// ═══════════════════════════════════════════════════════════════════
// PIPELINE TABLE INTERFACES
// ═══════════════════════════════════════════════════════════════════

/** Almacena el contenido crudo de cada archivo ingresado al sistema. */
export interface DBRawInput {
  dbId?: number;
  firmId: string;
  companyId: string;
  periodo: string;
  sourceType: 'xml' | 'excel' | 'pdf' | 'manual';
  fileName: string;
  rawContent: string;
  importedAt: string;
  batchId?: string;
}

/** Agrupa múltiples archivos procesados en una misma sesión de importación. */
export interface DBImportBatch {
  dbId?: number;
  firmId: string;
  companyId: string;
  periodo: string;
  batchId: string;
  totalFiles: number;
  processedFiles: number;
  status: 'pending' | 'processing' | 'done' | 'error';
  createdAt: string;
  completedAt?: string;
}

/** Documento parseado y estructurado listo para clasificación. */
export interface DBParsedDocument {
  dbId?: number;
  firmId: string;
  companyId: string;
  periodo: string;
  batchId?: string;
  documentType: 'invoice' | 'credit_note' | 'debit_note' | 'other';
  parsedData: string;
  rawInputId?: number;
  parsedAt: string;
}

/** Transacción canónica: versión normalizada y definitiva de una operación. */
export interface DBCanonicalTransaction {
  dbId?: number;
  firmId: string;
  companyId: string;
  periodo: string;
  transactionId: string;
  source: 'xml' | 'excel' | 'manual' | 'auto';
  data: string;
  createdAt: string;
}

/** Resultado de una regla de validación aplicada a una entidad. */
export interface DBValidationEvent {
  dbId?: number;
  firmId: string;
  companyId: string;
  entityType: 'transaction' | 'journalEntry' | 'sunatRecord';
  entityId: string;
  ruleName: string;
  passed: boolean;
  message?: string;
  validatedAt: string;
}

/** Registro inmutable de toda acción significativa sobre datos. */
export interface DBAuditEvent {
  dbId?: number;
  firmId: string;
  companyId: string;
  action: 'create' | 'update' | 'delete' | 'import' | 'export' | 'carryover';
  entityType: string;
  entityId?: string;
  userId?: string;
  oldValue?: string;
  newValue?: string;
  detail?: string;
  occurredAt: string;
}

// ═══════════════════════════════════════════════════════════════════
// ENTITIES TABLE
// ═══════════════════════════════════════════════════════════════════

/**
 * Proveedor o cliente con documento de identidad conocido.
 * Fuente de verdad para el RUC en el Formato 8.1 (campo 12).
 */
export interface DBEntity {
  dbId?: number;
  firmId: string;
  companyId: string;
  /** Tipo de documento (Tabla 2 SUNAT). '6'=RUC, '1'=DNI, '4'=CE, '0'=sin doc. */
  tipoDocIdentidad: string;
  /** Número de RUC u otro documento. 11 dígitos para RUC peruano. */
  numeroDoc: string;
  /** Razón social o apellidos/nombres tal como aparece en el comprobante. */
  razonSocial: string;
  /**
   * Nombre normalizado para búsqueda: minúsculas, sin tildes, sin puntuación.
   * Generado automáticamente por `normalizeForSearch()`.
   */
  razonSocialNorm: string;
  tipo: 'proveedor' | 'cliente' | 'both';
  cuentaPcge?: string;
  nombreCuentaPcge?: string;
  createdAt: string;
  updatedAt: string;
}

// ═══════════════════════════════════════════════════════════════════
// PERIOD BALANCES TABLE
// ═══════════════════════════════════════════════════════════════════

/**
 * Snapshot de saldo por cuenta PCGE para un período contable.
 * Pilar 3 — Gobernanza de Período.
 * Clave lógica única: [firmId + companyId + period + accountCode].
 */
export interface DBPeriodBalance {
  /** PK — bigserial in Supabase (returned as string via JSON). Optional for inserts. */
  id?: string;
  /** snake_case alias: firm_id */
  firmId: string;
  /** snake_case alias: company_id */
  companyId: string;
  /** Período en formato YYYYMM (sin guión). Ej: '202501'. snake_case alias: period */
  period: string;
  /** snake_case alias: account_code */
  accountCode: string;
  /** Decimal string. snake_case alias: initial_balance */
  initialBalance: string;
  /** Decimal string. snake_case alias: debits */
  debits: string;
  /** Decimal string. snake_case alias: credits */
  credits: string;
  /** finalBalance = initialBalance + debits − credits. Decimal string. snake_case alias: final_balance */
  finalBalance: string;
  /** snake_case alias: is_closed */
  isClosed: boolean;
  /** snake_case alias: closed_at */
  closedAt?: string;
  /** snake_case alias: closed_by */
  closedBy?: string;
  /** snake_case alias: journal_entry_ids */
  journalEntryIds: string[];
  /** snake_case alias: last_updated */
  lastUpdated: string;
  /** snake_case alias: created_by */
  createdBy?: string;
}

// ═══════════════════════════════════════════════════════════════════
// STATS & BATCH TYPES
// ═══════════════════════════════════════════════════════════════════

export interface DBStats {
  totalTransactions: number;
  totalJournalEntries: number;
  totalSunatRecords: number;
  totalDocuments: number;
  companies: number;
}

export interface PeriodBatch {
  periodo: string;
  entries: SerializedJournalEntry[];
  /** Si true, cierra el período al final de su procesamiento. */
  closePeriod?: boolean;
  closedBy?: string;
}

// ═══════════════════════════════════════════════════════════════════
// FEAT-9 — TEAM MEMBERS (multi-usuario)
// ═══════════════════════════════════════════════════════════════════

export interface DBTeamMember {
  id: string;
  firm_id: string;
  user_id: string;
  role: 'member' | 'readonly';
  status: 'accepted' | 'suspended';
  invited_by: string | null;
  accepted_at: string;
  created_at: string;
}

export interface DBTeamInvitation {
  id: string;
  firm_id: string;
  email: string;
  role: 'member' | 'readonly';
  token: string;
  status: 'pending' | 'accepted' | 'revoked' | 'expired';
  company_ids: string[];
  invited_by: string | null;
  expires_at: string;
  created_at: string;
}

// ═══════════════════════════════════════════════════════════════════
// PURE HELPERS (no Dexie dependency)
// ═══════════════════════════════════════════════════════════════════

/**
 * Normaliza un nombre para búsqueda insensible a tildes, mayúsculas
 * y separadores. Ej: "RIPLEY S.A." → "ripley sa".
 */
export function normalizeForSearch(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}
